import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, Link } from "react-router-dom";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  AlertCircle,
  Loader2,
  ArrowRight,
} from "lucide-react";
import logo from "../../assets/logo.png";
import loginImg from "../../assets/signin.png";
import { useAuth } from "../../context/AuthContext";
import { useLogin } from "../../lib/api/authOnboarding";

// Validation schema
const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

type LoginFormData = z.infer<typeof loginSchema>;
const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL;

export default function Login() {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);
  const [showPassword, setShowPassword] = useState(false);
  // const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({ resolver: zodResolver(loginSchema) });

  const navigate = useNavigate();
  const { mutateAsync: login, isPending } = useLogin();
  const { refreshAuthState } = useAuth();

  const onSubmit = async (data: LoginFormData) => {
    try {
      const response = await login({
        email: data.email,
        password: data.password,
      });

      setTimeout(() => {
        refreshAuthState();

        const user = response?.data?.user;
        const userRole = user?.role;
        const verification = user?.verification;

        // Check if user has pending verifications (for contributors only)
        const hasPendingVerification =
          userRole !== "admin" &&
          (user?.dateOfBirth ||
            verification?.ssnVerificationStatus === "not_started");

        if (userRole === "admin") {
          navigate("/admin/home");
        } else if (hasPendingVerification) {
          // Redirect to profile to complete verification
          navigate("/profile");
        } else {
          // Fully verified contributor - go to dashboard
          navigate("/");
        }
      }, 1500);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong. Please try again.";
      console.warn(message);
    }
  };

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Left Side - Image (Fixed) */}
      <div className="hidden lg:block lg:w-1/2 xl:w-[55%] relative h-screen">
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#8B52FF]/90 to-[#E05A6A]/90 z-10" />

        {/* Image */}
        <img
          src={loginImg}
          alt="Login"
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Content Overlay */}
        <div className="absolute inset-0 z-20 flex flex-col justify-between p-12 xl:p-16">
          {/* Top */}
          <div></div>

          {/* Center Content */}
          <div className="max-w-lg">
            <h2 className="text-4xl xl:text-5xl font-bold text-white mb-6 leading-tight">
              Shape the Future of AI
            </h2>
            <p className="text-lg text-white/80 leading-relaxed">
              Join thousands of contributors worldwide who are training the next
              generation of artificial intelligence models.
            </p>
          </div>

          {/* Bottom Stats */}
          <div className="flex items-center gap-8">
            <div>
              <div className="text-3xl font-bold text-white">50K+</div>
              <div className="text-white/70 text-sm">Contributors</div>
            </div>
            <div className="w-px h-12 bg-white/20" />
            <div>
              <div className="text-3xl font-bold text-white">10+</div>
              <div className="text-white/70 text-sm">Countries</div>
            </div>
            <div className="w-px h-12 bg-white/20" />
            <div>
              <div className="text-3xl font-bold text-white">$1M+</div>
              <div className="text-white/70 text-sm">Paid Out</div>
            </div>
          </div>
        </div>
      </div>

      {/* Richt Side - Form */}
      <div className="flex-1 flex flex-col bg-white h-screen">
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-6 lg:p-8 border-b border-gray-100">
          <Link to={FRONTEND_URL as string}>
            <img src={logo} alt="Unimerg" className="h-9 w-auto" />
          </Link>
        </div>

        {/* Scrollable Form Container */}
        <div className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-400">
          <div className="flex items-center justify-center min-h-full px-6 lg:px-12 xl:px-20 py-8">
            <div className="w-full max-w-sm">
              {/* Header */}
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Welcome back
                </h1>
                <p className="text-gray-500">
                  Enter your credentials to access your account
                </p>
              </div>

              {/* Error Message */}
              {/* {errorMessage && (
                <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{errorMessage}</p>
                </div>
              )} */}

              {/* Form */}
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      {...register("email")}
                      placeholder="Enter your email"
                      className={`w-full pl-12 pr-4 py-3.5 rounded-xl border bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF] transition-all ${
                        errors.email ? "border-red-300" : "border-gray-200"
                      }`}
                    />
                  </div>
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1.5 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" />
                      {errors.email.message}
                    </p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={showPassword ? "text" : "password"}
                      {...register("password")}
                      placeholder="Enter your password"
                      className={`w-full pl-12 pr-12 py-3.5 rounded-xl border bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF] transition-all ${
                        errors.password ? "border-red-300" : "border-gray-200"
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-red-500 text-sm mt-1.5 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" />
                      {errors.password.message}
                    </p>
                  )}
                </div>

                {/* Remember & Forgot */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-gray-300 text-[#8B52FF] focus:ring-[#8B52FF]"
                    />
                    <span className="text-sm text-gray-600">Remember me</span>
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-[#8B52FF] hover:text-[#E05A6A] font-medium transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isPending}
                  className="w-full py-3.5 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold rounded-xl shadow-lg shadow-[#8B52FF]/25 hover:shadow-xl hover:shadow-[#8B52FF]/30 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2 group"
                >
                  {isPending ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      Sign In
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
                    </>
                  )}
                </button>
              </form>

              {/* Divider */}
              {/* <div className="relative my-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center">
                  <span className="px-4 bg-white text-gray-400 text-sm">
                    or
                  </span>
                </div>
              </div> */}

              {/* Google */}
              {/* <button
                type="button"
                className="w-full py-3.5 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all flex items-center justify-center gap-3"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span className="text-gray-700 font-medium">
                  Continue with Google
                </span>
              </button> */}

              {/* Sign Up */}
              <p className="text-center text-gray-600 mt-8">
                Don't have an account?{" "}
                <Link
                  to="/signup"
                  className="text-[#8B52FF] hover:text-[#E05A6A] font-semibold transition-colors"
                >
                  Sign up
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
